import { useState, useEffect, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import theme from 'theme';
import { Portlet, PortletContent, PortletHeader, PortletLabel } from '../../components/Portlets';
import { Status } from '../../components/Status';
import { Cell } from '../../components/Cell';
import { STATUS_COLORS } from '../../constants/requestStatus';
import { formatDate, snapToArray } from '../../utils';
import { useFirestore } from '../../utils/firebase';
import { useAuth } from '../../auth';
import { apiClient } from '../../api-client/index.js';

const HardwareRequestsTable = ({ requests, setRequests }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user, getToken } = useAuth();
  const database = useFirestore();

  const loadRequests = useCallback(async () => {
    const queryRequests = query(
      collection(database, 'hardware_requests'),
      where('userId', '==', user.uid),
      orderBy('requestedAt', 'desc'),
    );

    try {
      const snapshot = await getDocs(queryRequests);
      const result = snapToArray(snapshot);
      setRequests(result);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [database, setRequests, user.uid]);

  const cancelRequest = useCallback(
    async id => {
      try {
        const result = await apiClient('POST', `/requests/hardware/${id}/status`, getToken, {
          status: 'canceled',
        });

        if (result?.status !== 'OK') {
          throw new Error(result?.message || 'Request failed');
        }

        enqueueSnackbar('Hardware request successfully canceled', {
          variant: 'success',
        });
        loadRequests();
      } catch (error) {
        enqueueSnackbar('Error when canceling hardware request', {
          variant: 'error',
        });
        console.error(error);
      }
    },
    [getToken, enqueueSnackbar, loadRequests],
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hideActionsColumnAndCell = requests =>
    requests.filter(request => request.status === 'pending').length > 0;

  useEffect(() => {
    setLoading(true);
    loadRequests();
  }, [loadRequests, user]);

  return (
    <Portlet
      sx={{ background: 'none', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      {requests.length ? (
        <PortletHeader noDivider>
          <PortletLabel
            subtitle={`${requests.length} in total`}
            title="Latest Requests"
            sx={{
              '@media (max-width:480px)': {
                '& h5': {
                  fontSize: '1.3rem',
                },
                '& h6': {
                  marginLeft: '0.5rem',
                  fontSize: '0.7rem',
                  width: '3rem',
                },
              },
            }}
          />
        </PortletHeader>
      ) : (
        ''
      )}
      <PerfectScrollbar>
        <PortletContent
          isLoading={isLoading}
          sx={{ minWidth: '600px', overflow: 'auto' }}
          noPadding
        >
          {requests.length ? (
            <>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    {hideActionsColumnAndCell(requests) && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {requests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(({ id, description, requestedAt, title, status = 'pending' }) => (
                      <TableRow sx={{ cursor: 'pointer' }} hover key={id} data-cy={description}>
                        <Cell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Status
                              sx={{ marginRight: theme.spacing(1) }}
                              color={STATUS_COLORS[status]}
                              size="sm"
                            />
                            {status}
                          </Box>
                        </Cell>
                        <Cell>{formatDate(requestedAt)}</Cell>
                        <Cell>{title}</Cell>
                        <Cell>{description}</Cell>
                        {hideActionsColumnAndCell(requests) && (
                          <Cell>
                            {status === 'pending' ? (
                              <Button
                                size="small"
                                onClick={() => {
                                  cancelRequest(id);
                                }}
                                sx={{
                                  backgroundColor: theme.palette.danger.main,
                                  color: theme.palette.common.white,
                                  '&:hover': {
                                    backgroundColor: '#c62828',
                                  },
                                }}
                                data-cy="cancelButton"
                              >
                                CANCEL
                              </Button>
                            ) : (
                              <div />
                            )}
                          </Cell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={requests.length}
                page={page}
              />
            </>
          ) : (
            <Box sx={{ padding: '24px', paddingTop: 0, fontWeight: 300, color: '#777' }}>
              No hardware requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default HardwareRequestsTable;
